import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import {
  CoreFlightSteps,
  FlightsNavigationFlow,
} from "@b2bportal/core-utilities";
import {
  FilterDialog,
  Filters,
  FlightList,
  FlightListError,
  FlightListGridFilter,
  FlightListLoading,
  FlightShopTitle,
  Sort,
  useTrackEventRuntime,
} from "@tenant/flights";
import { Page } from "@tenant/ui";
import clsx from "clsx";
import { useFlightSlot } from "../../slots";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEventOnce,
} from "../../tracking";
import defaultStyles from "./DeparturePage.module.scss";
import type { DeparturePageProps } from "./types";

const COMPONENT_KEY = CoreFlightsComponent.DeparturePage;

export const DeparturePage = ({
  children,
  className,
  context,
  handlers,
  text,
}: DeparturePageProps) => {
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const currentStep = FlightsNavigationFlow.useGetCurrentStep();

  const FlightSlots = useFlightSlot();

  const {
    isLoading,
    tripCategory,
    destination,
    sortOption,
    isError,
    flightList,
    isInitializing,
    isAllCollapsed,
  } = context;

  useEmitCoreFlightRawEventOnce(
    {
      name: FlightRawEventName.EnteredFlight,
      properties: { first_launch: true },
    },
    isInitializing === false // Wait for state to populate when reloading
  );

  useTrackEventRuntime(
    {
      name: FlightRawEventName.ViewedFlightListLoading,
      properties: {},
    },
    isLoading
  );

  const {
    handleSortChange,
    handleFareSelect,
    handleChooseFare,
    onErrorReload,
    setIsAllCollapsed,
  } = handlers;

  return (
    <Page
      title={text.pageTitle}
      subtitle={text.subtitle}
      className={clsx(block, className)}
    >
      <div className={cn("container")}>
        <div className={cn("search")}>
          <FlightSlots.FlightShopSearch />
        </div>
        <div className={cn("filter")}>
          <Filters sliceIndex={0} />
        </div>

        {!isLoading && (
          <>
            <FlightSlots.FlightShopBreadcrumbs
              className={cn("breadcrumbs")}
              currentStep={currentStep ?? CoreFlightSteps.DEPARTURE}
            />
            <FlightShopTitle
              className={cn("title-container")}
              tripCategory={tripCategory}
              destination={destination}
            />

            <div className={cn("divider-container")}>
              <div className={cn("divider")} />
            </div>
            <div className={cn("sort")}>
              <Sort value={sortOption} handleSortChange={handleSortChange} />
            </div>
            <div className={cn("all-filters")}>
              <FilterDialog sliceIndex={0} />
            </div>

            <FlightListGridFilter className={cn("grid-filter")} />

            {isError ? (
              <FlightListError
                className={cn("error")}
                onErrorReload={onErrorReload}
              />
            ) : (
              <div
                className={clsx(
                  cn("flight-list-container", {
                    initializing: isInitializing ?? false,
                  })
                )}
              >
                <FlightList
                  flightList={flightList}
                  sliceIndex={0}
                  onFareSelect={handleFareSelect}
                  goToNextStep={handleChooseFare}
                  className={cn("flight-list")}
                  isAllCollapsed={isAllCollapsed}
                  setIsAllCollapsed={setIsAllCollapsed}
                />
              </div>
            )}
          </>
        )}
        <FlightListLoading isLoading={isLoading || isInitializing} />
        {children}
      </div>
    </Page>
  );
};
